import colors from '../global/color';
import font from '../global/typography';
import elevation from '../global/elevation';
import spacing from '../global/spacing';

// inspired from chakra UI
export const duration = {
	shortest: 150,
	shorter: 200,
	short: 250,
	// most basic recommended timing
	standard: 300,
	// this is to be used in complex animations
	complex: 375,
	// recommended when something is entering screen
	enteringScreen: 225,
	// recommended when something is leaving screen
	leavingScreen: 195,
};

// inspired from chakra UI
export const easing = {
	// This is the most common easing curve.
	easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
	// Objects enter the screen at full velocity from off-screen and
	// slowly decelerate to a resting point.
	easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
	// Objects leave the screen at full velocity. They do not decelerate when off-screen.
	easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
	// The sharp curve is used by objects that may return to the screen at any time.
	sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
};

/**
 * Calculates the duration for auto height animation based on the provided height.
 * @param {number} height - The height of the element being animated.
 * @return {number} The duration for the auto height animation.
 */
function getAutoHeightDuration(height) {
	if (!height) {
		return 0;
	}

	const constant = height / 36;

	// https://www.wolframalpha.com/input/?i=(4+%2B+15+*+(x+%2F+36+)+**+0.25+%2B+(x+%2F+36)+%2F+5)+*+10
	return Math.round((4 + 15 * constant ** 0.25 + constant / 5) * 10);
}

const create = (props = ['all'], options = {}) => {
	const {
		duration: durationOption = duration.standard,
		easing: easingOption = easing.easeInOut,
		delay = 0,
	} = options;
	return (Array.isArray(props) ? props : [props])
		.map(
			(animatedProp) =>
				`${animatedProp} ${
					typeof durationOption === 'string'
						? durationOption
						: `${durationOption}ms`
				} ${easingOption} ${
					typeof delay === 'string' ? delay : `${delay}ms`
				}`
		)
		.join(',');
};

// const hover = (key) => {
//   const bg = {
//     [colors.neutral100]: colors.neutral100,
//     [colors.neutral900]: colors.neutral900,
//     [colors.neutral900]: colors.neutral900,
//     [colors.neutral100]: colors.neutral100,
//     [colors.brand]: colors.bgPrimaryHover,
//     [colors.neutral100]: colors.neutral100,
//     [colors.disabledText]: colors.disabledText,
//     [colors.neutral300]: colors.neutral300,
//     [colors.neutral100]: colors.neutral100,
//     [colors.link2]: colors.link2Hover,
//     [colors.neutral100]: colors.neutral100,
//     [colors.neutral900]: colors.neutral900,
//     [colors.neutral100]: colors.neutral100,
//     [colors.disabledText]: colors.disabledText,
//     [colors.neutral300]: colors.neutral300,
//   };
//   return bg[key];
// };

// const zIndex = {
//   drawer: 1200,
//   modal: 1300,
//   popOver: 1300,
//   tooltip: 1500,
//   sideNav: 100,
//   toolbar: 50
// }

// const counter = {
//   enabled: {
//     stroke: colors.neutral900,
//   },
//   disabled: {
//     stroke: colors.neutral500,
//   },
// };

const BaseTheme = {
	color: {
		button: {
			primary: colors.brand_60,
			primary_hover: colors.brand_70,
			primary_pressed: colors.brand_80,
			primary_text: colors.gray_05,
			primary_outline: colors.brand_70,
			primary_outline_text: colors.brand_70,
			primary_outline_hover: colors.brand_05,
			primary_outline_pressed: colors.brand_10,
			secondary: colors.gray_20,
			secondary_hover: colors.gray_30,
			secondary_pressed: colors.gray_40,
			secondary_text: colors.gray_100,
			secondary_outline: colors.gray_70,
			secondary_outline_hover: colors.gray_05,
			secondary_outline_pressed: colors.gray_10,
			secondary_outline_text: colors.gray_70,
			tertiary: colors.gray_80,
			tertiary_hover: colors.gray_90,
			tertiary_pressed: colors.gray_95,
			tertiary_text: colors.gray_80,
			tertiary_hover_text: colors.gray_90,
			tertiary_pressed_text: colors.gray_95,
		},
		input: {
			background: colors.white,
			background_hover: colors.gray_05,
			background_active: colors.White,
			background_error: colors.red_05,
			line: colors.gray_30,
			line_hover: colors.gray_40,
			line_active: colors.brand_60,
			line_error: colors.red_50,
		},
		radio: {
			normal: colors.gray_70,
			hover_stroke: colors.brand_60,
			hover_fill: colors.brand_05,
			active_fill: colors.brand_10,
			disable_stroke: colors.gray_40,
			disable_filled: colors.gray_20,
			text: colors.brand_60,
		},
		stroke: {
			light: colors.gray_20,
			medium: colors.gray_30,
			tooltip: colors.gray_100,
		},
		separator: {
			primary: colors.gray_40,
			secondary: colors.gray_30,
			tertiary: colors.gray_20,
			brand: colors.brand_60,
			neutral_6: colors.neutral_6,
		},
		alert: {
			background_warning: colors.yellow_10,
			background_error: colors.red_10,
			background_violet: colors.brand_10,
			background_success: colors.green_10,
			text_warning: colors.yellow_70,
			text_error: colors.red_70,
			text_success: colors.green_70,
			text_violet: colors.brand_60,
		},
		text: {
			primary: colors.gray_100,
			secondary: colors.gray_70,
			tertiary: colors.white,
			disabled: colors.gray_40,
			highlight: colors.gray_20,
			hyperlink: colors.brand_60,
			hyperlink_hover: colors.brand_80,
			hyperlink_focus: colors.brand_60,
			error: colors.red_70,
			success: colors.green_50,
			neutral: colors.neutral,
			warning: colors.yellow_70,
			more: colors.blue_60,
		},
		icon: {
			primary: colors.gray_70,
			secondary: colors.gray_40,
			white: colors.white,
			brand: colors.brand_60,
			online: colors.green_50,
			brand_outline: colors.brand_10,
			brand_20: colors.brand_20,
			brand_40: colors.brand_40,
			brand_80: colors.brand_80,
			red_10: colors.red_10,
			red_40: colors.red_40,
			disabled: colors.disabled,
			green_20: colors.green_20,
			red_100: colors.red_100,
			grey_65: colors.grey_65,
			red_110: colors.red_110,
			purple_01: colors.purple_01,
			blue_01: colors.blue_01,
			yellow_01: colors.yellow_01,
			green_90: colors.green_90,
			gray_100: colors.gray_100,
			error: colors.red_60,
			onlineDot: colors.onlineDot,
			offlineDot: colors.offlineDot,
			red_70: colors.red_70,
		},
		background: {
			primary: colors.white,
			secondary: colors.gray_05,
			tertiary: colors.gray_10,
			quaternary: colors.gray_20,
			brand: colors.brand_60,
			brand_outline: colors.brand_10,
			error: colors.red_60,
			iconFill: colors.black,
		},
	},
	FontFace: {
		font_size: {
			xxxxx_large: font.size.xxxxx_large,
			xxxx_large: font.size.xxxx_large,
			xx_large: font.size.xx_large,
			xxx_large: font.size.xxx_large,
			x_large: font.size.x_large,
			large: font.size.large,
			l_medium: font.size.l_medium,
			x_medium: font.size.x_medium,
			medium: font.size.medium,
			small_medium: font.size.small_medium,
			small: font.size.small,
			input_text: font.size.input_text,
			x_small: font.size.x_small,
			xx_inputlabel: font.size.xx_inputlabel,
			xx_small: font.size.xx_small,
			xxx_small: font.size.xxx_small,
			xxxx_small: font.size.xxxx_small,
			button: font.size.button,
			xx_medium: font.size.xx_medium,
		},
		font_weight: {
			light: font.weight.light,
			regular: font.weight.regular,
			medium: font.weight.medium,
			bold: font.weight.bold,
			semiBold: font.weight.semiBold,
		},
		line_height: {
			reset: font.lh.reset,
			small: font.lh.small,
			medium: font.lh.medium,
			large: font.lh.large,
		},
	},
	elevation: {
		shadow0: elevation.shadow0,
		shadow1: elevation.shadow1,
		shadow2: elevation.shadow2,
		shadow3: elevation.shadow3,
		shadow4: elevation.shadow4,
	},
	spacing: {
		base_unit_none: spacing.base_unit_none,
		base_unit_1: spacing.base_unit_1,
		base_unit_2: spacing.base_unit_2,
		base_unit_3: spacing.base_unit_3,
		base_unit_4: spacing.base_unit_4,
		base_unit_5: spacing.base_unit_5,
		base_unit_6: spacing.base_unit_6,
		base_unit_7: spacing.base_unit_7,
		base_unit_8: spacing.base_unit_8,
		base_unit_9: spacing.base_unit_9,
		base_unit_10: spacing.base_unit_10,
	},
	transitions: {
		duration,
		easing,
		create,
		getAutoHeightDuration,
	},
	zIndex: {
		drawer: 1200,
		modal: 1300,
		popOver: 1300,
		tooltip: 1500,
		sideNav: 100,
		toolbar: 50,
	},
};

export default BaseTheme;
